import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TicketData } from "../../entry/server/app.ts";
import { AppContext } from "../../context/app-context.tsx";
import { appRepo } from "../../repo/app-repo.ts";
import { Pagination } from "../../lib/api.ts";
import PaginationView from "../../componrnts/pagination.tsx";

function getStatusMeta(status: string) {
  const normalized = (status || "").toLowerCase();
  if (normalized === "pending") {
    return { label: "درحال انتظار", className: "bg-sky-500/20 text-sky-300" };
  }
  if (normalized === "answered") {
    return { label: "پاسخ داده شده", className: "bg-indigo-500/20 text-indigo-300" };
  }
  if (normalized === "closed") {
    return { label: "بسته شده", className: "bg-emerald-500/20 text-emerald-300" };
  }
  return { label: status || "-", className: "bg-slate-700/60 text-slate-300" };
}

function getPriorityMeta(priority: string) {
  const normalized = (priority || "").toLowerCase();
  if (normalized === "low") {
    return { label: "کم", className: "bg-slate-700/60 text-slate-300" };
  }
  if (normalized === "medium") {
    return { label: "متوسط", className: "bg-blue-500/20 text-blue-300" };
  }
  if (normalized === "high") {
    return { label: "بالا", className: "bg-orange-500/20 text-orange-300" };
  }
  if (normalized === "urgent") {
    return { label: "فوری", className: "bg-rose-500/20 text-rose-300" };
  }
  return { label: priority || "-", className: "bg-slate-700/60 text-slate-300" };
}

export default function TicketsList() {
  const [tickets, setTickets] = useState<TicketData[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const { setMessage } = useContext(AppContext);

  const statusFilters = [
    { value: "all", label: "همه وضعیت‌ها" },
    { value: "pending", label: "درحال انتظار" },
    { value: "answered", label: "پاسخ داده شده" },
    { value: "closed", label: "بسته شده" },
  ];

  const priorityFilters = [
    { value: "all", label: "همه اولویت‌ها" },
    { value: "low", label: "کم" },
    { value: "medium", label: "متوسط" },
    { value: "high", label: "بالا" },
    { value: "critical", label: "فوری" },
  ];
  
  useEffect(() => {
    const fetchTickets = async () => {
      setLoading(true);
      try {
        const response = await appRepo.getTickets(page, selectedStatus, selectedPriority);
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت لیست تیکت‌ها ناموفق بود." });
          setTickets([]);
          return;
        }
        setTickets(response.data);
        setPagination(response.pagination || null);
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [page, selectedStatus, selectedPriority, setMessage]);

  if (loading) {
    return (
      <section className="mt-4 space-y-4">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت لیست تیکت‌ها...
        </div>
      </section>
    );
  }

  return (
    <section className="mt-4 space-y-4">
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-2">
            <p className="text-xs text-slate-400">فیلتر وضعیت</p>
            <div className="flex flex-wrap gap-2">
              {statusFilters.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setSelectedStatus(item.value)}
                  className={`rounded-full px-3 py-1.5 text-xs transition ${
                    selectedStatus === item.value
                      ? "bg-indigo-500 text-white"
                      : "border border-slate-700 text-slate-300 hover:border-indigo-400 hover:text-indigo-300"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs text-slate-400">فیلتر اولویت</p>
            <div className="flex flex-wrap gap-2">
              {priorityFilters.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setSelectedPriority(item.value)}
                  className={`rounded-full px-3 py-1.5 text-xs transition ${
                    selectedPriority === item.value
                      ? "bg-indigo-500 text-white"
                      : "border border-slate-700 text-slate-300 hover:border-indigo-400 hover:text-indigo-300"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>



      <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60">
        <table className="min-w-full divide-y divide-slate-800 text-sm">
          <thead className="bg-slate-900/80">
            <tr>
              <th className="px-4 py-3 text-right font-medium text-slate-300">شناسه</th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">عنوان</th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">کاربر</th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">اولویت</th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">وضعیت</th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">آخرین بروزرسانی</th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">عملیات</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {tickets.map((ticket) => {
              const priorityMeta = getPriorityMeta(ticket.priority);
              const statusMeta = getStatusMeta(ticket.status);
              return (
                <tr key={ticket.id} className="transition hover:bg-slate-900">
                  <td className="whitespace-nowrap px-4 py-3 text-slate-300">{ticket.id}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-100">{ticket.title}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-300">{ticket.username}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${priorityMeta.className}`}>
                      {priorityMeta.label}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusMeta.className}`}>
                      {statusMeta.label}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-300 ltr text-right">{ticket.updated_at}</td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/tickets/${ticket.id}/reply`}
                    state={{ ticket }}
                      className="inline-flex rounded-lg border border-indigo-700 px-3 py-1.5 text-xs text-indigo-200 transition hover:border-indigo-500 hover:text-indigo-300"
                    >
                      پاسخ به تیکت
                    </Link>
                  </td>
                </tr>
              );
            })}
            {!tickets.length && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-sm text-slate-400">
                  تیکتی با فیلترهای انتخاب‌شده یافت نشد.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {pagination && <PaginationView pagination={pagination} onPageChange={setPage} />}
    </section>
  );
}
